import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Users, Car, Gauge, Calendar, Mail, Loader2, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import BookingCalendar from '../components/BookingCalendar';
import CollectionAddress from '../components/CollectionAddress';
import { publicAPI } from '../services/api';
import useAPI from '../hooks/useAPI';
import SEO, { generateMotorhomeSchema, generateBreadcrumbSchema } from '../components/SEO';

// Booking calendar configuration for each motorhome
const bookingConfig = {
  'roller-team-740-manual': { categoryId: '49050', unitId: '50253' },
  'roller-team-740-automatic': { categoryId: '49050', unitId: '52177' },
  'roller-team-747': { categoryId: '49050', unitId: '53154' },
  'roller-team-707': { categoryId: '49050', unitId: '52178' },
  'rollerteam-zefiro-696': { categoryId: '49050', unitId: '47426' },
  'rollerteam-zefiro-707': { categoryId: '49050', unitId: '48146' },
  'rollerteam-zefiro-sport': { categoryId: '49050', unitId: '84165' },
};

const MotorhomeDetail = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const openBooking = searchParams.get('book') === '1';
  const { data: allMotorhomes, loading, error } = useAPI(() => publicAPI.getMotorhomes(), []);
  const motorhome = allMotorhomes?.find(m => m.slug === slug);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showCalendar, setShowCalendar] = useState(openBooking);
  
  // Get booking config for this motorhome
  const calendarConfig = bookingConfig[slug];

  // Reset calendar visibility when slug changes
  useEffect(() => {
    setShowCalendar(openBooking);
    setSelectedImage(0);
  }, [slug, openBooking]);

  useEffect(() => {
    if (openBooking && motorhome) {
      setTimeout(() => {
        document.getElementById('booking-calendar-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [openBooking, motorhome]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading motorhome details...</p>
        </div>
      </div>
    );
  }

  if (error || !motorhome) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Motorhome Not Found</h2>
          <Button asChild>
            <Link to="/motorhomes">Back to Fleet</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title={`${motorhome.name} Hire | ${motorhome.berth} Berth ${motorhome.specifications?.transmission || ''} Motorhome Hire Suffolk & Essex`}
        rawTitle
        description={`Hire ${motorhome.name} - ${motorhome.berth} berth, ${motorhome.seatBelts} belt ${motorhome.type} motorhome from £${motorhome.pricePerNight}/night in Suffolk. Insurance, unlimited mileage & free parking included. ${motorhome.availableFrom ? `Available from ${motorhome.availableFrom} - pre-book now.` : 'Check live availability and book online.'}`}
        keywords={`${motorhome.name}, ${motorhome.berth} berth motorhome hire, ${motorhome.type} motorhome hire, Roller Team hire Suffolk, motorhome hire Essex`}
        url={`/motorhomes/${motorhome.slug}`}
        image={motorhome.images?.[0]}
        type="product"
        structuredData={[generateMotorhomeSchema(motorhome), generateBreadcrumbSchema([{ name: 'Motorhomes', path: '/motorhomes' }, { name: motorhome.name, path: `/motorhomes/${motorhome.slug}` }])]}
      />
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button variant="ghost" asChild>
            <Link to="/motorhomes">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Fleet
            </Link>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            <div className="sticky top-24">
              <div className="rounded-2xl overflow-hidden shadow-2xl mb-4">
                <img
                  src={motorhome.images[selectedImage] + '?w=800'}
                  alt={motorhome.name}
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                {motorhome.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`rounded-lg overflow-hidden transition-all ${
                      selectedImage === idx
                        ? 'ring-4 ring-blue-600 scale-105'
                        : 'hover:scale-105 opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img + '?w=300'}
                      alt={`${motorhome.name} view ${idx + 1}`}
                      className="w-full h-24 object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="mb-6">
              <div className="flex gap-2 mb-4">
                <Badge className="bg-blue-600 text-white">{motorhome.berth} Berth</Badge>
                <Badge className="bg-slate-700 text-white capitalize">{motorhome.type}</Badge>
                {motorhome.availableFrom ? (
                  <Badge className="bg-purple-600 text-white flex items-center gap-1 animate-pulse">
                    <Calendar className="h-3 w-3" />
                    Book Now for {motorhome.availableFrom}!
                  </Badge>
                ) : motorhome.available !== false ? (
                  <Badge className="bg-green-500 text-white flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Available Now
                  </Badge>
                ) : (
                  <Badge className="bg-amber-500 text-white flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Check Dates
                  </Badge>
                )}
              </div>
              <h1 className="text-4xl font-bold text-slate-900 mb-4">{motorhome.name}</h1>
              
              {/* Book Now for Future Date Banner */}
              {motorhome.availableFrom && (
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl p-5 mb-4 shadow-lg">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 rounded-full p-3">
                      <Calendar className="h-8 w-8" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-xl">Book Now for {motorhome.availableFrom} Onwards!</p>
                      <p className="text-purple-100">This brand new motorhome is accepting bookings now for hire dates from {motorhome.availableFrom}. Secure your adventure today!</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-lg text-slate-600">From</span>
                <span className="text-5xl font-bold text-blue-600">£{motorhome.pricePerNight}</span>
                <span className="text-xl text-slate-600">per night</span>
              </div>
            </div>

            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Specifications</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-slate-600">Sleeps</p>
                      <p className="font-semibold">{motorhome.specifications.sleeps} people</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Car className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-slate-600">Transmission</p>
                      <p className="font-semibold capitalize">{motorhome.specifications.transmission}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Gauge className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-slate-600">Length</p>
                      <p className="font-semibold">{motorhome.specifications.length}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <div>
                      <p className="text-sm text-slate-600">Fuel</p>
                      <p className="font-semibold">{motorhome.specifications.fuelType}</p>
                    </div>
                  </div>
                  {motorhome.specifications.seatbelts && (
                    <div className="flex items-center gap-3">
                      <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <div>
                        <p className="text-sm text-slate-600">Seat Belts</p>
                        <p className="font-semibold">{motorhome.specifications.seatbelts} travelling</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Description</h2>
                <p className="text-slate-700 leading-relaxed">{motorhome.description}</p>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Key Features</h2>
                <ul className="space-y-3">
                  {motorhome.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Layout/Floorplan Section */}
            {motorhome.layoutImage && (
              <Card className="mb-6 overflow-hidden" data-testid="interior-layout-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-100 rounded-lg p-2">
                      <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Interior Layout</h2>
                  </div>
                  <p className="text-slate-600 mb-4">See how the space is organized inside this motorhome</p>
                  <div className="bg-slate-900 rounded-xl p-4 overflow-hidden">
                    <img 
                      src={motorhome.layoutImage} 
                      alt={`${motorhome.name} Interior Layout`}
                      className="w-full h-auto object-contain rounded-lg"
                      data-testid="interior-layout-image"
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Front</p>
                      <p className="font-semibold text-slate-800">Cab Seats</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Middle</p>
                      <p className="font-semibold text-slate-800">Living & Kitchen</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Rear</p>
                      <p className="font-semibold text-slate-800">
                        {motorhome.features.some(f => f.toLowerCase().includes('bunk')) ? 'Bunk Beds' : 'Fixed Double Bed'}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Bathroom</p>
                      <p className="font-semibold text-slate-800">Washroom & WC</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA Buttons */}
            <div className="space-y-4">
              {calendarConfig && (
                <Button 
                  size="lg" 
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => setShowCalendar(!showCalendar)}
                  data-testid="check-availability-btn"
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  {showCalendar ? 'Hide Calendar' : 'Check Availability & Book Online'}
                </Button>
              )}
              <Button size="lg" className="w-full" asChild>
                <a href={`mailto:milliesmotorhomehire@gmail.com?subject=Booking Enquiry - ${motorhome.name}`}>
                  <Mail className="mr-2 h-5 w-5" />
                  Enquire About Booking
                </a>
              </Button>
              <Button size="lg" variant="outline" className="w-full" asChild>
                <a href="tel:07527006966">
                  Call Us: 07527 006966
                </a>
              </Button>
            </div>

            {/* Online Booking Calendar */}
            {calendarConfig && showCalendar && (
              <Card className="mt-6 scroll-mt-28" id="booking-calendar-card" data-testid="booking-calendar-card">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Calendar className="h-6 w-6 text-blue-600" />
                    Check Availability & Book
                  </h2>
                  <p className="text-slate-600 mb-4">
                    Select your dates below to check availability and book this motorhome online.
                  </p>
                  <BookingCalendar 
                    unitId={calendarConfig.unitId} 
                    categoryId={calendarConfig.categoryId} 
                  />
                </CardContent>
              </Card>
            )}

            {/* Collection & Drop-off Times */}
            <Card className="mt-6 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-green-600" />
                  Standard Collection & Drop-off Times
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-3 text-center border border-green-200">
                    <p className="text-xs text-slate-600">FREE Collection</p>
                    <p className="text-2xl font-bold text-green-600">3:00 PM</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-amber-200">
                    <p className="text-xs text-slate-600">FREE Drop-off</p>
                    <p className="text-2xl font-bold text-amber-600">11:00 AM</p>
                  </div>
                </div>
                <p className="text-xs text-slate-600 text-center mb-4">
                  Need different times? Extend via the <strong>extras screen</strong> during booking
                </p>
                <CollectionAddress compact />
              </CardContent>
            </Card>

            {/* Pet-Friendly Info */}
            <Card className="mt-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <svg className="h-5 w-5 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  Pets Welcome!
                </h3>
                <p className="text-sm text-slate-600 mb-3">Well-behaved pets are part of the family</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-white rounded-lg p-2 text-center border border-amber-200">
                    <p className="text-xs text-slate-600">1 Pet</p>
                    <p className="text-xl font-bold text-amber-600">£20</p>
                  </div>
                  <div className="bg-white rounded-lg p-2 text-center border border-amber-200">
                    <p className="text-xs text-slate-600">2 Pets</p>
                    <p className="text-xl font-bold text-amber-600">£40</p>
                  </div>
                </div>
                <p className="text-xs text-slate-600 text-center">
                  Select via <strong>extras screen</strong> during booking
                </p>
              </CardContent>
            </Card>

            <Card className="mt-4 bg-blue-50 border-blue-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-2">What's Included:</h3>
                <ul className="space-y-1 text-sm text-slate-700">
                  <li>✓ Unlimited mileage</li>
                  <li>✓ Comprehensive insurance</li>
                  <li>✓ Full kitchen equipment</li>
                  <li>✓ 24/7 breakdown support</li>
                </ul>
                <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                  <p className="font-semibold text-amber-800">Optional Extras:</p>
                  <p className="text-amber-700">Bedding & towels not included - £30/set</p>
                </div>
                
                {/* Free Car Parking */}
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    <div>
                      <p className="font-semibold text-green-800 text-sm">Free Car Parking</p>
                      <p className="text-xs text-green-700">Leave your car safely with us while on hire</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Similar Motorhomes */}
        <section className="mt-20">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Similar Motorhomes</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {(allMotorhomes || [])
              .filter(m => m.slug !== motorhome.slug && m.berth === motorhome.berth)
              .slice(0, 3)
              .map((m) => (
                <Card key={m.id} className="overflow-hidden hover:shadow-xl transition-all">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={m.images[0] + '?w=400'}
                      alt={m.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-2">{m.name}</h3>
                    <p className="text-slate-600 text-sm mb-4 capitalize">{m.type}</p>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="text-sm text-slate-600">From</span>
                        <span className="text-2xl font-bold text-blue-600 ml-2">£{m.pricePerNight}</span>
                      </div>
                      <span className="text-slate-600 text-sm">per night</span>
                    </div>
                    <Button asChild className="w-full">
                      <Link to={`/motorhomes/${m.slug}`}>View Details</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default MotorhomeDetail;
